import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Clock, Plus, BadgeCheck, Lock, Video, FileText, Image as ImageIcon, X, DollarSign, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const NICHES = ["all", "affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];
const EMPTY = { title: "", description: "", niche: "affiliate", content: "", cover_url: "", duration_min: 5, is_paid: false, price: 0, videos: [], pdfs: [], slides: [] };

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [niche, setNiche] = useState("all");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(null); // "video" | "pdf" | "slide" | "cover" | null
  const [uploadPct, setUploadPct] = useState(0);
  const videoRef = useRef(); const pdfRef = useRef(); const slideRef = useRef(); const coverRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const q = niche === "all" ? "" : `?niche=${niche}`;
      const { data } = await api.get(`/lessons${q}`);
      setLessons(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [niche]);

  const uploadFile = async (file, kind) => {
    if (!file) return;
    setUploading(kind); setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0, maxContentLength: Infinity, maxBodyLength: Infinity,
        onUploadProgress: (e) => e.total && setUploadPct(Math.round((e.loaded / e.total) * 100)),
      });
      const entry = { url: data.url, name: data.filename || file.name, size: data.size };
      if (kind === "cover") {
        setForm((f) => ({ ...f, cover_url: data.url }));
      } else {
        setForm((f) => {
          const key = kind === "video" ? "videos" : kind === "pdf" ? "pdfs" : "slides";
          return { ...f, [key]: [...f[key], entry] };
        });
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); setUploadPct(0); }
  };

  const removeAttachment = (kind, idx) => {
    const key = kind === "video" ? "videos" : kind === "pdf" ? "pdfs" : "slides";
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  };

  const create = async () => {
    if (!form.title.trim() || !form.content.trim()) return alert("Title and content required.");
    if (form.is_paid && (!form.price || Number(form.price) <= 0)) return alert("Set a price for paid lessons.");
    await api.post("/lessons", { ...form, price: Number(form.price || 0) });
    setForm(EMPTY); setCreating(false); load();
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <GraduationCap size={22} className="text-[#00E676]" strokeWidth={2} />
              <h1 className="font-heading font-bold text-4xl tracking-tight">Lessons</h1>
            </div>
            <p className="text-sm text-white/45 font-mono mt-1">Bite-size playbooks from real operators.</p>
          </div>
          <button
            data-testid="new-lesson-btn"
            onClick={() => setCreating(!creating)}
            className="cta-green rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
          >
            <Plus size={14} strokeWidth={2.5} /> {creating ? "Cancel" : "New lesson"}
          </button>
        </div>

        <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="lessons-filters">
          {NICHES.map((n) => (
            <button
              key={n}
              data-testid={`lesson-filter-${n}`}
              onClick={() => setNiche(n)}
              className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-md border transition ${
                niche === n
                  ? "border-[#00E676] text-[#00E676] bg-[#00E676]/8"
                  : "border-white/8 text-white/45 hover:text-white"
              }`}
            >
              {n}
            </button>
          ))}
        </div>

        {creating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="surface rounded-xl p-5 mb-6 space-y-3">
            <input data-testid="new-lesson-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Lesson title" className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 focus:outline-none focus:border-[#00E676]/40" />
            <input data-testid="new-lesson-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short description (public — visible before purchase)"
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40" />
            <textarea data-testid="new-lesson-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="The full playbook — markdown supported. (Hidden until purchase for paid lessons.)" rows={6}
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#00E676]/40" />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="new-lesson-niche" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })}
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40">
                {NICHES.filter(n => n !== "all").map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input data-testid="new-lesson-duration" type="number" value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} placeholder="duration (min)"
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40" />
            </div>

            {/* Cover image uploader — shows preview, used as the lesson thumbnail in the grid */}
            <div className="surface-elev rounded-md p-3 flex items-center gap-3">
              <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" data-testid="new-lesson-cover-input"
                onChange={(e) => uploadFile(e.target.files?.[0], "cover")} />
              {form.cover_url ? (
                <div className="relative">
                  <img src={form.cover_url} alt="" className="w-28 h-16 object-cover rounded-md border border-white/10" />
                  <button onClick={() => setForm({ ...form, cover_url: "" })}
                    className="absolute -top-1 -right-1 bg-black/80 rounded-full p-0.5 hover:bg-black"
                    data-testid="remove-cover">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <div className="w-28 h-16 rounded-md border border-dashed border-white/15 flex items-center justify-center">
                  <ImageIcon size={18} className="text-white/30" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-xs font-mono uppercase tracking-wider text-white/40 mb-1">Cover image</div>
                <div className="text-[11px] text-white/50 mb-2">Shows as the thumbnail in the lessons grid.</div>
                <button data-testid="new-lesson-cover-upload" onClick={() => coverRef.current?.click()}
                  disabled={uploading}
                  className="cta-light rounded-md px-3 py-1.5 text-xs font-bold disabled:opacity-50">
                  {form.cover_url ? "Replace cover" : "Upload cover"}
                </button>
              </div>
            </div>

            {/* Free / Paid toggle */}
            <div className="flex items-center gap-2 surface-elev rounded-md p-3">
              <button
                data-testid="lesson-free"
                onClick={() => setForm({ ...form, is_paid: false, price: 0 })}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition ${
                  !form.is_paid ? "bg-white text-black" : "text-white/50 hover:text-white"
                }`}
              >
                Free
              </button>
              <button
                data-testid="lesson-paid"
                onClick={() => setForm({ ...form, is_paid: true })}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition flex items-center gap-1.5 ${
                  form.is_paid ? "cta-green" : "text-white/50 hover:text-white"
                }`}
              >
                <DollarSign size={12} strokeWidth={2.5} /> Paid
              </button>
              {form.is_paid && (
                <div className="relative ml-auto">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00E676] font-mono">$</span>
                  <input data-testid="lesson-price" type="number" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="0" min="0" step="0.01"
                    className="w-32 bg-white/[0.04] border border-white/8 rounded-md pl-7 pr-3 py-1.5 font-mono text-sm text-[#00E676] focus:outline-none focus:border-[#00E676]/40" />
                </div>
              )}
              {form.is_paid && (
                <span className="text-[10px] font-mono text-white/40 uppercase tracking-wider hidden sm:inline">
                  · You approve each buyer manually
                </span>
              )}
            </div>

            {/* Attachments */}
            <div className="surface-elev rounded-md p-3 space-y-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">Attachments (gated for paid lessons)</div>

              <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0], "video")} data-testid="lesson-video-input" />
              <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0], "pdf")} data-testid="lesson-pdf-input" />
              <input ref={slideRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0], "slide")} data-testid="lesson-slide-input" />

              <div className="flex flex-wrap items-center gap-1.5">
                <button data-testid="lesson-add-video" onClick={() => videoRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <Video size={13} className="text-[#00E676]" /> Add video
                </button>
                <button data-testid="lesson-add-pdf" onClick={() => pdfRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <FileText size={13} className="text-[#00E676]" /> Add PDF
                </button>
                <button data-testid="lesson-add-slide" onClick={() => slideRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <ImageIcon size={13} className="text-[#00E676]" /> Add slide image
                </button>
              </div>

              {uploading && (
                <div className="flex items-center gap-3 text-xs text-white/70">
                  <Loader2 size={14} className="animate-spin text-[#00E676]" />
                  <div className="flex-1">
                    <div className="flex justify-between mb-1"><span>Uploading {uploading}…</span><span className="font-mono text-[#00E676]">{uploadPct}%</span></div>
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-[#00E676] transition-all" style={{ width: `${uploadPct}%` }} />
                    </div>
                  </div>
                </div>
              )}

              {(form.videos.length + form.pdfs.length + form.slides.length) > 0 && (
                <div className="space-y-1">
                  {form.videos.map((v, i) => (
                    <AttachRow key={`v-${i}`} icon={Video} label={v.name} size={v.size} onRemove={() => removeAttachment("video", i)} testid={`lesson-video-${i}`} />
                  ))}
                  {form.pdfs.map((p, i) => (
                    <AttachRow key={`p-${i}`} icon={FileText} label={p.name} size={p.size} onRemove={() => removeAttachment("pdf", i)} testid={`lesson-pdf-${i}`} />
                  ))}
                  {form.slides.map((s, i) => (
                    <AttachRow key={`s-${i}`} icon={ImageIcon} label={s.name} size={s.size} onRemove={() => removeAttachment("slide", i)} testid={`lesson-slide-${i}`} />
                  ))}
                </div>
              )}
            </div>

            <button data-testid="new-lesson-publish" onClick={create} className="cta-green rounded-md px-5 py-2 text-sm font-bold">
              Publish lesson
            </button>
          </motion.div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-xl h-72 shimmer" />)}</div>
        ) : lessons.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <GraduationCap size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">No lessons here yet.</div>
            <div className="text-sm text-white/50">Be the first to teach this niche.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {lessons.map((l, i) => (
              <motion.div key={l.lesson_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/lessons/${l.lesson_id}`} className="surface rounded-xl overflow-hidden flex flex-col card-hover h-full group relative" data-testid={`lesson-${l.lesson_id}`}>
                  {l.cover_url ? (
                    <img src={l.cover_url} alt="" className="h-36 w-full object-cover grayscale opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition" />
                  ) : (
                    <div className="h-36 grid-bg bg-[#0F0F0F] flex items-center justify-center">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">{l.niche}</span>
                    </div>
                  )}
                  {l.is_paid && (
                    <div className="absolute top-2 right-2 cta-green rounded-md px-2 py-1 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1 font-bold">
                      {l.has_access ? <><BadgeCheck size={11} /> Unlocked</> : <><Lock size={11} /> ${l.price}</>}
                    </div>
                  )}
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-white/40">
                      <span>{l.niche}</span><span>·</span><Clock size={10} /> {l.duration_min} min
                      {l.completed_by_me && <span className="ml-auto text-[#00E676] flex items-center gap-0.5"><BadgeCheck size={11} /> Done</span>}
                    </div>
                    <h3 className="font-heading font-semibold text-lg leading-snug">{l.title}</h3>
                    <p className="text-xs text-white/55 line-clamp-2 leading-relaxed">{l.description}</p>
                    <div className="mt-auto text-[11px] text-white/40 font-mono">by {l.author?.name || "unknown"}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const AttachRow = ({ icon: Icon, label, size, onRemove, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2 surface rounded-md px-3 py-2 text-xs">
    <Icon size={13} className="text-[#00E676] shrink-0" />
    <span className="flex-1 truncate font-mono">{label}</span>
    {size > 0 && <span className="text-white/40 font-mono shrink-0">{(size / (1024 * 1024)).toFixed(1)}MB</span>}
    <button onClick={onRemove} className="text-white/40 hover:text-red-400 transition shrink-0"><X size={12} /></button>
  </div>
);
